import React, { useState, useEffect } from "react";
import cx from "clsx";
import VisibilitySensor from "react-visibility-sensor";
import { Container, Row, Col } from "react-grid-system";
import { Parallax } from "react-scroll-parallax";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

import TextFromLeft from "@/components/shared/text/textFromLeft";
import { VIS_OFFSET, VIS_MIN_TOP } from "@/constants";
import { getContainerWidth } from "@/helpers";

import globalStyles from "@/styles/global.module.scss";
import sectionStyles from "./sections.module.scss";
import styles from "./section3.module.scss";
import { isTablet } from "react-device-detect";

const renderTextFromLeft = (title, delayMs, props) => {
  const { isVisible } = props;

  return (
    <TextFromLeft
      visible={isVisible}
      delayMs={delayMs}
      className={cx(sectionStyles.title, globalStyles.right)}
    >
      <h3 className={sectionStyles.title}>{title}</h3>
    </TextFromLeft>
  );
};

const renderTitle = props => {
  const { title } = props;

  return (
    <Row gutterWidth={0}>
      <Col xs={12} className={globalStyles.foreground}>
        {title[0] ? renderTextFromLeft(title[0], 0, props) : null}
        {title[1] ? renderTextFromLeft(title[1], 180, props) : null}
        {title[2] ? renderTextFromLeft(title[2], 360, props) : null}
        {title[3] ? renderTextFromLeft(title[3], 540, props) : null}
      </Col>
    </Row>
  );
};

const renderSlash = props => {
  const { isDesktop, screenClass } = props;

  const x = isDesktop ? 7 : screenClass === "md" ? 5 : 6;

  return (
    <Row>
      <Col xs={12 - x} />
      <Col xs={x} className={globalStyles.foreground}>
        <div className={globalStyles.slashContainer}>
          <div className={globalStyles.forwardSlash} />
        </div>
      </Col>
    </Row>
  );
};

const renderParagraph = props => {
  const { paragraph, button, screenClass, isDesktop, windowWidth } = props;

  const containerWidth = getContainerWidth(screenClass, windowWidth);
  const maxWidth = isDesktop ? containerWidth * 0.5 : undefined;

  return (
    <div
      className={cx(
        globalStyles.pContainer,
        globalStyles.foreground,
        globalStyles.contentPaddingH,
        isDesktop && styles.isDesktop,
        styles.content
      )}
      style={{ maxWidth }}
    >
      <Container className={globalStyles.containerMax}>
        {paragraph}

        {!isDesktop && !isTablet && button ? (
          <div
            className={cx(
              sectionStyles.buttonContainer,
              globalStyles.foreground,
              styles.buttonContainer,
              styles.mobile
            )}
          >
            {button}
          </div>
        ) : null}
      </Container>
    </div>
  );
};

const renderBgImg = props => {
  const { bgImg, bgImgY, bgImgClassName } = props;

  return bgImg ? (
    <div className={cx(styles.bgImgContainer, bgImgClassName)}>
      <Parallax y={bgImgY || ["-100px", "300px"]} className={styles.bgImg}>
        <div className={globalStyles.background}>{bgImg}</div>
      </Parallax>
    </div>
  ) : null;
};

const renderSlickPaging = i => {
  return (
    <div className={styles.slickDotContainer}>
      <span className={styles.slickDot} />
    </div>
  );
};

const renderRight = props => {
  const {
    images,
    button,
    isVisible,
    isDesktop,
    screenClass,
    imageClassName
  } = props;

  return (
    <div
      className={cx(
        !isDesktop && screenClass !== "md" && styles.contentPaddingL,
        styles.rightContainer
      )}
    >
      <div className={styles.gallery}>
        <div className={cx(globalStyles.foreground, imageClassName)}>
          <Slider
            key={`section3-slider-${isVisible ? "visible" : "invis"}`}
            dots
            autoplay={isVisible}
            infinite
            arrows={false}
            autoplaySpeed={3000}
            dotsClass={cx("slick-dots", styles.slickDots)}
            speed={500}
            centerMode={true}
            slidesToShow={1}
            slidesToScroll={1}
            customPaging={renderSlickPaging}
          >
            {images}
          </Slider>
        </div>
      </div>

      {(isDesktop || isTablet) && button ? (
        <div
          className={cx(
            sectionStyles.buttonContainer,
            globalStyles.foreground,
            styles.buttonContainer,
            styles.desktop
          )}
        >
          {button}
        </div>
      ) : null}
    </div>
  );
};

const Section3 = props => {
  const { isDesktop, containerClassName } = props;

  const [hasMounted, setMounted] = useState(false);
  const [isVisible, setVisible] = useState(false);
  const setIsVisible = visible =>
    visible && !isVisible ? setVisible(true) : null;

  useEffect(() => {
    if (!hasMounted) setMounted(true);

    if (!isVisible) return;

    //
  }, [isVisible]); // eslint-disable-line

  return (
    <VisibilitySensor
      delayedCall
      partialVisibility
      offset={VIS_OFFSET}
      minTopValue={VIS_MIN_TOP}
      onChange={setIsVisible}
    >
      <div
        className={cx(
          sectionStyles.section,
          styles.container,
          !isDesktop && styles.mobile,
          containerClassName
        )}
      >
        <Row gutterWidth={30}>
          <Col xs={12} md={6} className={globalStyles.midground}>
            <div className={cx(globalStyles.maxWidth, styles.titleContainer)}>
              {renderTitle({ ...props, hasMounted, isVisible, isDesktop })}
            </div>

            {renderSlash({ ...props, hasMounted, isVisible, isDesktop })}

            {isDesktop || isTablet
              ? renderParagraph({
                  ...props,
                  hasMounted,
                  isVisible,
                  isDesktop
                })
              : null}
          </Col>
          <Col xs={12} md={6} className={globalStyles.midground}>
            {renderRight({ ...props, hasMounted, isVisible, isDesktop })}
            {!isDesktop && !isTablet
              ? renderParagraph({
                  ...props,
                  hasMounted,
                  isVisible,
                  isDesktop
                })
              : null}
          </Col>
        </Row>
        {renderBgImg(props)}
      </div>
    </VisibilitySensor>
  );
};

export default Section3;
